import React, { useState, useEffect } from "react";
import WeatherConditions from "./WeatherConditions";

const WeatherApp = ({ latitude, longitude, locationName, onLocationChange }) => {
  const [locationInput, setLocationInput] = useState(locationName);
  const [latInput, setLatInput] = useState(latitude.toString());
  const [lonInput, setLonInput] = useState(longitude.toString());
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [error, setError] = useState("");

  // Sync props to local state when they change
  useEffect(() => {
    setLocationInput(locationName);
    setLatInput(latitude.toString());
    setLonInput(longitude.toString());
  }, [latitude, longitude, locationName]);

  const handleLocationLookup = async () => {
    if (!locationInput.trim()) {
      setError("Please enter a location name");
      return;
    }

    setIsLookingUp(true);
    setError("");

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(locationInput)}&format=json&limit=1`
      );
      const data = await response.json();

      if (data.length === 0) {
        setError("Location not found");
        setIsLookingUp(false);
        return;
      }

      const location = data[0];
      onLocationChange({
        latitude: parseFloat(location.lat),
        longitude: parseFloat(location.lon),
        locationName: locationInput,
      });
      setLatInput(parseFloat(location.lat).toString());
      setLonInput(parseFloat(location.lon).toString());
      setError("");
    } catch (error) {
      setError("Error looking up location");
      console.error("Geocoding error:", error);
    } finally {
      setIsLookingUp(false);
    }
  };

  return (
    <div className="w-full h-full flex flex-col bg-white">
      {/* Location Control Bar */}
      <div
        className="flex gap-2 p-2 border-b items-center flex-wrap"
        style={{
          background: "#c0c0c0",
          borderBottomColor: "#808080",
        }}
      >
        <div className="flex gap-2 items-center flex-1 min-w-60">
          <label className="text-xs font-bold" style={{ color: "#000000" }}>
            Location:
          </label>
          <input
            type="text"
            value={locationInput}
            onChange={(e) => {
              setLocationInput(e.target.value);
              setError("");
            }}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleLocationLookup();
              }
            }}
            className="flex-1 px-2 py-1 text-xs border"
            style={{
              background: "#ffffff",
              color: "#000000",
              borderColor: "#808080",
            }}
            placeholder="Enter city name..."
          />
          <button
            onClick={handleLocationLookup}
            disabled={isLookingUp}
            className="px-3 py-1 text-xs font-bold border-2"
            style={{
              background: "#c0c0c0",
              borderTopColor: "#dfdfdf",
              borderLeftColor: "#dfdfdf",
              borderRightColor: "#808080",
              borderBottomColor: "#808080",
              cursor: isLookingUp ? "wait" : "pointer",
              opacity: isLookingUp ? 0.6 : 1,
            }}
          >
            {isLookingUp ? "..." : "Find"}
          </button>
        </div>

        {/* Coordinate Inputs */}
        <div className="flex gap-2 items-center">
          <label className="text-xs font-bold" style={{ color: "#000000" }}>
            Lat:
          </label>
          <input
            type="number"
            value={latInput}
            onChange={(e) => setLatInput(e.target.value)}
            step="0.0001"
            className="w-24 px-2 py-1 text-xs border"
            style={{
              background: "#ffffff",
              color: "#000000",
              borderColor: "#808080",
            }}
            placeholder="Latitude"
          />
          <label className="text-xs font-bold" style={{ color: "#000000" }}>
            Lon:
          </label>
          <input
            type="number"
            value={lonInput}
            onChange={(e) => setLonInput(e.target.value)}
            step="0.0001"
            className="w-24 px-2 py-1 text-xs border"
            style={{
              background: "#ffffff",
              color: "#000000",
              borderColor: "#808080",
            }}
            placeholder="Longitude"
          />
          <button
            onClick={() => {
              onLocationChange({
                latitude: parseFloat(latInput),
                longitude: parseFloat(lonInput),
                locationName: locationInput,
              });
            }}
            className="px-3 py-1 text-xs font-bold border-2"
            style={{
              background: "#c0c0c0",
              borderTopColor: "#dfdfdf",
              borderLeftColor: "#dfdfdf",
              borderRightColor: "#808080",
              borderBottomColor: "#808080",
              cursor: "pointer",
            }}
          >
            Update
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div
          className="px-2 py-1 text-xs"
          style={{
            background: "#ffcccc",
            color: "#cc0000",
            borderBottom: "1px solid #ff0000",
          }}
        >
          {error}
        </div>
      )}

      {/* Weather Content */}
      <div className="flex-1 overflow-auto">
        <WeatherConditions
          latitude={latitude}
          longitude={longitude}
          locationName={locationName}
        />
      </div>
    </div>
  );
};

export default WeatherApp;
